import { useState } from "react";
import { Box, Container } from "@mui/material";

import Header1 from "../Header/Header1";
import BackgroundEffects from "./Backgroundeffects";
import HeroContent from "./Herocontent";
import HeroButtons from "./Herobuttons";
import HeroStats from "./Herostats";
import HeroVisualization from "./Herovisualization";
import TalkToExpertsDialog from "./Talktoexpertsdialog";
import { HEADER_HEIGHT, FONTS } from "./Hero.styles";

/**
 * EnterpriseHeroV2
 *
 * Brand-new hero component. Does NOT modify or import anything from the
 * original EnterpriseHero — this is a fully separate, self-contained tree.
 *
 * Layout: two-column on desktop (content ~55% / visualization ~45%),
 * stacked and centered on mobile. Uses a flexbox Box split (matching the
 * pattern used elsewhere in this codebase) rather than MUI Grid, to avoid
 * any risk of API mismatch across MUI major versions (Grid's item/size
 * props changed between v5 and v6/v7).
 *
 * Height: minHeight: calc(100dvh - headerHeight), NOT height: 100vh.
 * This lets the hero grow if content needs more room (e.g. very large
 * zoom levels or long translations) instead of clipping or hiding overflow.
 * It will never overlap Header1 because Header1's own 64px spacer pushes
 * this component down before it even renders, and this minHeight only
 * defines a floor, not a ceiling.
 *
 * Dialog state and "Talk to Experts" logic are owned locally — this
 * component is fully self-contained and can be dropped into any page
 * with zero external wiring beyond the import itself.
 */
export default function EnterpriseHeroV2() {
    const [dialogOpen, setDialogOpen] = useState(false);

    const handleExploreSolutions = () => {
        const section = document.getElementById("our-solutions");
        if (section) section.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <>
            {/*
        Font loading: the original hero pulled Bricolage Grotesque + DM Sans
        via a <style> @import in its Styles component. EnterpriseHeroV2 is
        fully decoupled from that component, so it loads its own fonts here.
        If these fonts are already loaded globally elsewhere in the app
        (e.g. in index.html or a global stylesheet), this link is a harmless
        no-op duplicate — browsers dedupe identical font requests.
      */}
            <link
                rel="stylesheet"
                href="https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400;12..96,600;12..96,750;12..96,800&family=DM+Sans:wght@300;400;500;600&display=swap"
            />

            <Header1 />

            <TalkToExpertsDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />

            <Box
                component="section"
                aria-label="Enterprise AI platform overview"
                sx={{
                    position: "relative",
                    boxSizing: "border-box",
                    overflow: "hidden",
                    backgroundColor: "#000000",
                    color: "#ffffff",
                    fontFamily: FONTS.display,
                    display: "flex",
                    alignItems: "center",
                    // Floor, not a fixed ceiling — content can grow past this if needed.
                    minHeight: {
                        xs: `calc(100dvh - ${HEADER_HEIGHT.xs}px)`,
                        md: `calc(100dvh - ${HEADER_HEIGHT.md}px)`,
                        lg: `calc(100dvh - ${HEADER_HEIGHT.lg}px)`,
                    },
                }}
            >
                <BackgroundEffects />

                <Container
                    maxWidth="xl"
                    sx={{
                        position: "relative",
                        zIndex: 2,
                        px: { xs: "20px", sm: "32px", md: "48px", lg: "64px", xl: "80px" },
                        py: { xs: "56px", sm: "64px", md: "72px", lg: "64px", xl: "72px" },
                        maxWidth: { xl: "1440px" },
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", lg: "row" },
                            alignItems: "center",
                            gap: { xs: "48px", lg: "32px" },
                        }}
                    >
                        {/* Left column — content (~55%) */}
                        <Box sx={{ width: "100%", flex: { lg: "0 0 55%" }, maxWidth: { lg: "55%" } }}>
                            <HeroContent />
                            <HeroButtons
                                onTalkToExperts={() => setDialogOpen(true)}
                                onExploreSolutions={handleExploreSolutions}
                            />
                            <HeroStats />
                        </Box>

                        {/* Right column — visualization (~45%) */}
                        <Box sx={{ width: "100%", flex: { lg: "0 0 45%" }, maxWidth: { lg: "45%" } }}>
                            <HeroVisualization />
                        </Box>
                    </Box>
                </Container>
            </Box>
        </>
    );
}